import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, defer, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { TypeAPI } from '../../api/type-api';
import { ToastService } from '../toast-service';
import { Country } from '../../models/types/dto/country';
import { State } from '../../models/types/dto/state';

@Injectable({
  providedIn: 'root'
})
export class GeographyService {
  private typeAPI = inject(TypeAPI);
  private toastService = inject(ToastService);

  private _countriesLoading = new BehaviorSubject(false);
  public countriesLoading$ = this._countriesLoading.asObservable();

  public statesLoading$ = this.countriesLoading$;

  private cachedCountries = new BehaviorSubject<Country[] | null>(null);
  private inFlightCountries$: Observable<Country[]> | null = null;

  /**
   * Countries back every address form in the app, so a failed load must not be cached — each new
   * subscription re-issues the request until one succeeds, after which the result is reused.
   */
  public countries$: Observable<Country[]> = defer(() =>
    this.cachedCountries.pipe(
      take(1),
      switchMap(cachedCountries => (cachedCountries ? of(cachedCountries) : this.loadCountries$()))
    )
  );

  public statesForCountry(countryId: number | null): Observable<State[]> {
    return this.countries$.pipe(map(countries => countries.find(country => country.id === countryId)?.states ?? []));
  }

  private loadCountries$(): Observable<Country[]> {
    if (!this.inFlightCountries$) {
      this._countriesLoading.next(true);
      this.inFlightCountries$ = this.typeAPI.getCountriesWithStates().pipe(
        tap(countries => {
          this.cachedCountries.next(countries);
          this.inFlightCountries$ = null;
          this._countriesLoading.next(false);
        }),
        catchError(e => {
          this.inFlightCountries$ = null;
          this._countriesLoading.next(false);
          this.toastService.publishErrorMessage(
            $localize`Error`,
            $localize`An error occurred fetching countries. Please try again later.`
          );
          return throwError(() => e);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.inFlightCountries$;
  }
}
